import { isValid, isWeekend, parseISO } from 'date-fns';
import { z } from 'zod';

import { EmployeeIdpType } from '@/common/graphql';

export const IDP_KEY_AREA_MIN = 3;
export const IDP_KEY_AREA_MAX = 5;
export const HR_REPRESENTATIVE_DEFAULT_DEPARTMENT_NAME = 'People';

export const idpKeyAreaSchema = z.object({
  id: z.string().nullable(),
  performanceRecoveryArea: z
    .string()
    .refine(value => value.trim().length > 0, 'Key area for performance recovery is required'),
  successCriteria: z.string().refine(value => value.trim().length > 0, 'Success criteria is required'),
  additionalSupportNeeded: z.string().refine(value => value.trim().length > 0, 'Additional support needed is required'),
});

export type IdpKeyAreaFormData = z.infer<typeof idpKeyAreaSchema>;

export const emptyIdpKeyArea: IdpKeyAreaFormData = {
  id: null,
  performanceRecoveryArea: '',
  successCriteria: '',
  additionalSupportNeeded: '',
};

export const idpKeyAreasFormSchema = z.object({
  keyAreas: z
    .array(idpKeyAreaSchema)
    .min(IDP_KEY_AREA_MIN, `There are at least ${IDP_KEY_AREA_MIN} key areas.`)
    .max(IDP_KEY_AREA_MAX, `There are at most ${IDP_KEY_AREA_MAX} key areas.`),
});

export type IdpKeyAreasFormValues = z.infer<typeof idpKeyAreasFormSchema>;

export const idpReviewCommentsFormSchema = z.object({ comments: z.record(z.string()) }).superRefine((data, ctx) => {
  for (const [keyAreaId, comment] of Object.entries(data.comments)) {
    if (comment.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Review comment is required',
        path: ['comments', keyAreaId],
      });
    }
  }
});

export type IdpReviewCommentsFormValues = z.infer<typeof idpReviewCommentsFormSchema>;

export const idpFormSchema = z.object({
  employeeId: z.string().min(1, 'Employee is required'),
  hrRepresentativeId: z.string().min(1, 'HR representative is required'),
  type: z.nativeEnum(EmployeeIdpType),
  startDate: z
    .string()
    .min(1, 'Start date is required')
    .refine(value => {
      const parsed = parseISO(value);
      return isValid(parsed) && !isWeekend(parsed);
    }, 'Start date must be a weekday (Monday-Friday)'),
  hiddenFromEmployee: z.boolean(),
});

export type IdpFormData = z.infer<typeof idpFormSchema>;

/**
 * Form default values. The schema itself enforces required fields, so
 * `idpFormSchema.parse({})` cannot double as the defaults factory here.
 */
export const idpFormDefaults = (): IdpFormData => ({
  employeeId: '',
  hrRepresentativeId: '',
  type: EmployeeIdpType.Idp,
  startDate: '',
  hiddenFromEmployee: false,
});
